'use client';

import React from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { redirect } from 'next/navigation';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import LoginPopup from '@/app/popup/login-popup';
import { AdvancedImage } from '@cloudinary/react';

// Import our organized components and hooks
import { ChatLayout, UnifiedChatPanel } from './components/ChatLayout';
import { VideoStream } from './components/VideoStream';
import { DesktopChatLoading, Error, ImageLoadingTimer } from './components/LoadingStates';

import { useGlobalChatState } from '../../hooks/useGlobalChatState';
import { LiveImageStatus } from '../../data/liveMessageData/liveMessage';
import { ChatStatus } from '../../data/globalChatState';

export default function DesktopChatPage({
  params,
  initiateVideoRoom,
}: {
  params: Promise<{ avatarId: string }>;
  initiateVideoRoom: () => Promise<void>;
}) {
  // Use global chat state for character data and state management
  const [globalState, globalActions] = useGlobalChatState();
  const { 
    character: character, 
    isCharacterLoading: isLoading, 
    characterLoadingError: loadingError,
    chatStatus: chatStatus 
  } = globalState;

  const { isAuthenticated } = useAuth();

  const avatarId = character?.metadata.avatar_id || '';
  // Login popup state
  const [showLoginPopup, setShowLoginPopup] = React.useState(false);
  const [loginRedirectUrl, setLoginRedirectUrl] = React.useState('/');

  // Handle not-found case in useEffect to avoid hook ordering issues
  React.useEffect(() => {
    if (loadingError === 'NOT_FOUND') {
      redirect('/not-found'); // Redirect to the new not-found page with navbar
    }
  }, [loadingError]);

  // Auto-collapse sidebar when entering chat
  React.useEffect(() => {
    const collapseNavbar = () => {
      // Find the navbar collapse button and click it
      const navbar = document.querySelector('nav');
      if (navbar) {
        // Check if navbar is not already collapsed
        const isCurrentlyCollapsed = navbar.offsetWidth <= 80;
        if (!isCurrentlyCollapsed) {
          // Find the collapse button (the MenuIcon button when expanded)
          const collapseButton = navbar.querySelector('button[class*="w-8 h-8"]');
          if (collapseButton) {
            (collapseButton as HTMLButtonElement).click();
          }
        }
      }
    };

    // Delay to ensure DOM is ready
    const timer = setTimeout(collapseNavbar, 100);
    
    return () => clearTimeout(timer);
  }, []); // Only run once when component mounts

  // Show UI immediately when avatar data is available
  if (isLoading) {
    return <DesktopChatLoading/>;
  }

  // Handle not-found case specifically (redirect is called in useEffect above)
  if (loadingError === 'NOT_FOUND') {
    return <Error error="Character not found" />;
  }

  // Other error states  
  if (loadingError) {
    return <Error error={loadingError} />;
  }

  // Avatar should be available here since not-found case is handled above
  if (!character) {
    return <DesktopChatLoading />;
  }

  // Show UI immediately - chat history is already loaded with character data
  // Video mode - New layout design
  if (chatStatus !== ChatStatus.INFO) {
    return (
      <div className="h-full w-full bg-[#121214]">
        <div className="w-full h-full">
            <ChatLayout>
          {/* Video Section - Left */}
          {chatStatus !== ChatStatus.LIVE ? (
            // If streaming is turned off, show the active Cloudinary image
            globalState.activeImage.loadingStatus === LiveImageStatus.LOADED ? (
              <div className="relative w-full lg:w-auto lg:h-full aspect-[9/16] rounded-l-[5px] shadow-lg flex-shrink-0 overflow-hidden">
                <AdvancedImage
                  cldImg={globalState.activeImage.imageData}
                  alt="Character image"
                  className="object-cover w-full h-full"
                />
              </div>
            ) : (
              <div className="relative w-full lg:w-auto lg:h-full aspect-[9/16] rounded-l-[5px] shadow-lg flex-shrink-0 bg-gray-800 animate-pulse flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-gray-600 border-t-gray-400 rounded-full animate-spin" />
              </div>
            )
          ) : (
            // If  streaming is turned on, show the video stream
            <div className="relative w-full lg:w-auto lg:h-full aspect-[9/16] flex-shrink-0">
              <VideoStream />
            </div>
          )}
          
          {/* Chat Section - Right - Using Unified Component */}
          <UnifiedChatPanel
          />
        </ChatLayout>
        
              {/* Login Popup */}
      <LoginPopup 
        isOpen={showLoginPopup} 
        onClose={() => {
          setShowLoginPopup(false);
        }}
        redirectTarget={loginRedirectUrl}
      />
        </div>
      </div>
    );
  }

  // Regular chat mode (non-video) - Using Unified Component
  return (
    <div className="h-full w-full bg-[#121214]">
      <div className="w-full h-full">
        <ChatLayout>
        {/* Character Image */}
        {globalState.activeImage.loadingStatus === LiveImageStatus.LOADED ? (
          <div className="relative w-full lg:w-auto lg:h-full aspect-[9/16] rounded-l-[5px] shadow-lg flex-shrink-0 overflow-hidden">
            <AdvancedImage
              cldImg={globalState.activeImage.imageData}
              alt="Character image"
              className="object-cover w-full h-full"
            />
          </div>
        ) : (
          <div className="relative w-full lg:w-auto lg:h-full aspect-[9/16] rounded-l-[5px] shadow-lg flex-shrink-0 bg-gray-800 animate-pulse flex items-center justify-center">
            <div className="w-16 h-16 border-4 border-gray-600 border-t-gray-400 rounded-full animate-spin" />
          </div>
        )}
        {/* Chat Section - Using Unified Component */}
        <UnifiedChatPanel
          onStartChat={async () => {
            console.log('Start chat clicked, isAuthenticated:', isAuthenticated);
            if (!isAuthenticated) {
              setLoginRedirectUrl(`/chat/${avatarId}`);
              setShowLoginPopup(true);
              return;
            }
            console.log('Starting video mode for avatar:', avatarId);
            // Explicitly call initiateVideoRoom once when button is clicked
            await initiateVideoRoom();
          }}
        />
      </ChatLayout>
      
      {/* Login Popup */}
      <LoginPopup 
        isOpen={showLoginPopup} 
        onClose={() => {
          setShowLoginPopup(false);
        }}
        redirectTarget={loginRedirectUrl}
      />
      </div>
    </div>
  );
} 